import React, { useContext, useEffect, useMemo } from "react";
import styled from "styled-components";
import CenterContainer from "../components/CenterContainer";
import { Form, RawFormError, RawFormInput, FormLabel, FormRow, FormText, FormSelect, FormCheck, FormDate } from "../components/FormElements";
import Title from "../components/Title";
import nl from "date-fns/locale/nl";
import Button from "../components/Button";
import { Translation, useLanguage } from "../useLanguage";
import { faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";
import { Link, Prompt, useHistory } from "react-router-dom";
import AuthContext from "../AuthContext";
import { UserUpdateRequest, UserUpdateRequestSchema } from "consumerhouse-shared";
import { AnyListener, Listener, useForm } from "typed-react-form";
import { StatusContext } from "../StatusContext";
import { LanguageValues } from "consumerhouse-shared";
import { ButtonContainer } from "../components/ButtonContainer";
import { LinkButton } from "../components/LinkButton";
import { RateLimitError } from "../api";

const DontForget = styled.p`
    font-weight: bold;
    font-size: 0.9em;
    opacity: 0.7;
    text-align: right;
`;

export function EditAccount() {
    const language = useLanguage();
    const history = useHistory();
    const { client, auth, setAuth } = useContext(AuthContext);
    const form = useForm(
        {
            birthTimeStamp: auth!.user.birthTimeStamp,
            email: auth!.user.email,
            iban: auth!.user.iban,
            telephoneNumber: auth!.user.telephoneNumber ?? "",
            language: auth!.user.language ?? "nl",
            enableEmail: auth!.user.enableEmail,
            enableNotificationEmail: auth!.user.enableNotificationEmail,
        } as UserUpdateRequest,
        (data) => (UserUpdateRequestSchema.validate(data) as any) ?? {}
    );
    const status = useContext(StatusContext);

    async function submit() {
        await form.validate();
        if (form.error) return;
        form.setState({ isSubmitting: true });
        try {
            let res = await client.fetch("/user/update", form.values);
            if (res.status === "error") {
                form.setErrors({
                    email: res.email ? language(res.email as any) : undefined,
                    iban: res.iban ? language(res.iban as any) : undefined,
                });
            } else {
                setAuth({
                    accessToken: auth!.accessToken,
                    refreshToken: auth!.refreshToken,
                    user: res.user,
                });
                history.replace("/account");
            }
        } catch (ex) {
            if (ex instanceof RateLimitError) {
                form.setError("email", "login-limit");
            } else {
                console.error("Could not update account", ex);
            }
        }

        form.setState({ isSubmitting: false });
    }

    useEffect(() => {
        return () => {
            if (form.dirty) submit();
        };
    }, []);

    return (
        <CenterContainer width="500px" style={{ margin: "2em 0" }}>
            {/* <Prompt message={language("unsaved")} when={form.dirty} /> */}
            <Form
                onSubmit={(ev) => {
                    ev.preventDefault();
                    submit();
                }}>
                <Title>{language("edit-account")}</Title>
                <FormRow>
                    <FormLabel>{language("register-email")}</FormLabel>
                    <FormText form={form} name="email" style={{ flexGrow: 1 }} />
                </FormRow>
                <FormRow>
                    <FormLabel>{language("register-iban")}</FormLabel>
                    <FormText form={form} placeholder="BExx xxxx xxxx xxxx" name="iban" />
                </FormRow>
                <FormRow>
                    <FormLabel>{language("register-telephone")}</FormLabel>
                    <FormText form={form} placeholder="0032xxxxxxxxx" name="telephoneNumber" />
                </FormRow>
                {!status?.disableUserUpdateBirthDate && (
                    <FormRow>
                        <FormLabel>{language("register-birthdate")}</FormLabel>
                        <FormDate min="1900-01-01" max={new Date().toISOString().split("T")[0]} form={form} name="birthTimeStamp" />
                    </FormRow>
                )}
                <FormRow>
                    <FormLabel>{language("register-language")}</FormLabel>
                    <FormSelect form={form} name="language">
                        {LanguageValues.map((e) => (
                            <option key={e} value={e}>
                                {e.toUpperCase()}
                            </option>
                        ))}
                    </FormSelect>
                </FormRow>
                <FormRow>
                    <FormLabel>Regio</FormLabel>
                    <span style={{ marginRight: "0.7em", textTransform: "capitalize" }}>{auth!.user.activeRegionName}</span>
                    <Link to="/select-region">
                        <LinkButton>Aanpassen</LinkButton>
                    </Link>
                </FormRow>
                <Title>{language("edit-account-notifications")}</Title>
                <p>{language("email-preference-info")}</p>
                <FormRow>
                    <FormLabel>{language("email-preference-new-tests")}</FormLabel>
                    <FormCheck form={form} name="enableEmail" />
                </FormRow>
                <FormRow>
                    <FormLabel>{language("email-preference-notifications")}</FormLabel>
                    <FormCheck form={form} name="enableNotificationEmail" />
                </FormRow>
                <hr />
                <ButtonContainer>
                    <Button
                        type="button"
                        color="#666"
                        onClick={() => {
                            form.resetAll();
                            history.goBack();
                        }}
                        icon={faTimes}>
                        {language("edit-account-cancel")}
                    </Button>
                    <AnyListener
                        form={form}
                        render={({ state, error, dirty }) => (
                            <Button loading={state.isSubmitting} disabled={state.isSubmitting} type="submit" icon={faCheck} color="#56a44f">
                                {language("edit-account-finish")}
                            </Button>
                        )}
                    />
                </ButtonContainer>
            </Form>
        </CenterContainer>
    );
}
